import React, { useMemo } from "react";
import { CheckOutlined, MinusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Select, Space, Table, Tag, Typography } from "antd";

import {
  parsePermissionMap,
  PermissionScope,
  TeamMemberRole,
  type PermissionKeyEnum,
  type PermissionMap,
  type PermissionScopeEnum,
} from "@fitness-app/data-models";
import { type TeamMemberRoleEnum } from "@fitness-app/data-models/entities/TeamMember";

import { matchesPermissionRequirement } from "~/modules/Auth/permissionRequirement";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { permissionLabels, scopeLabels } from "~/modules/Team/permissionLabels";
import { supabase } from "~/store/initializeStore";
import { roleEditorAreas, type RoleEditorArea } from "./roleEditorAreas";

const EDITABLE_ROLES = [
  TeamMemberRole.SHARED_TRAINER,
  TeamMemberRole.REGULAR_TRAINER,
  TeamMemberRole.SALES_REP,
  TeamMemberRole.ADMINISTRATION,
  TeamMemberRole.SUPPORT,
] as const;

const roleLabels: Partial<Record<TeamMemberRoleEnum, string>> = {
  [TeamMemberRole.SHARED_TRAINER]: "Head coach",
  [TeamMemberRole.REGULAR_TRAINER]: "Trener",
  [TeamMemberRole.SALES_REP]: "Sprzedawca",
  [TeamMemberRole.ADMINISTRATION]: "Administracja",
  [TeamMemberRole.SUPPORT]: "Support",
};

interface CatalogEntry {
  key: string;
  description: string;
  grantableScopes: PermissionScopeEnum[];
}

interface BaselineRow {
  role: TeamMemberRoleEnum;
  key: string;
  scope: PermissionScopeEnum;
}

const NO_ACCESS_VALUE = "__none__";

const TeamRolesTab = (): React.ReactElement => {
  const queryClient = useQueryClient();

  const baselinesQuery = useQuery({
    queryKey: ["rolePermissionBaselines"],
    queryFn: async () => {
      const [catalogResult, baselineResult] = await Promise.all([
        supabase.from("permission").select("key, description, grantableScopes").order("key"),
        supabase.from("role_permission").select("role, key, scope"),
      ]);
      const error = catalogResult.error ?? baselineResult.error;
      if (error) {
        throw new Error(error.message);
      }
      return {
        catalog: (catalogResult.data ?? []) as CatalogEntry[],
        baselines: (baselineResult.data ?? []) as BaselineRow[],
      };
    },
  });

  const baselineMaps = useMemo(() => {
    const maps = new Map<TeamMemberRoleEnum, PermissionMap>();
    for (const role of EDITABLE_ROLES) {
      maps.set(
        role,
        parsePermissionMap(
          Object.fromEntries(
            (baselinesQuery.data?.baselines ?? [])
              .filter((row) => row.role === role)
              .map((row) => [row.key, row.scope]),
          ),
        ),
      );
    }
    return maps;
  }, [baselinesQuery.data]);

  const catalogByKey = useMemo(
    () => new Map((baselinesQuery.data?.catalog ?? []).map((entry) => [entry.key, entry])),
    [baselinesQuery.data],
  );

  const mutation = useMutation({
    mutationFn: async ({
      role,
      key,
      scope,
    }: {
      role: TeamMemberRoleEnum;
      key: PermissionKeyEnum;
      scope: PermissionScopeEnum | null;
    }) => {
      if (scope === null) {
        const { error } = await supabase.from("role_permission").delete().eq("role", role).eq("key", key);
        if (error) {
          throw new Error(error.message);
        }
        return;
      }
      const { error } = await supabase
        .from("role_permission")
        .upsert({ role, key, scope, updatedAt: new Date().toISOString() }, { onConflict: "role,key" });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["rolePermissionBaselines"] }),
  });

  const renderAreaCell = (area: RoleEditorArea, role: TeamMemberRoleEnum) => {
    const map = baselineMaps.get(role) ?? {};
    const visible = matchesPermissionRequirement(map, dashboardPermissionRequirements[area.area]);
    return visible ? <CheckOutlined style={{ color: "#25b10f" }} /> : <MinusOutlined style={{ color: "#bbb" }} />;
  };

  const renderScopeSelect = (permissionKey: PermissionKeyEnum, role: TeamMemberRoleEnum) => {
    const scope = baselineMaps.get(role)?.[permissionKey] ?? null;
    const grantable = (catalogByKey.get(permissionKey)?.grantableScopes ?? []).filter(
      (candidate) => candidate !== PermissionScope.NONE,
    );

    return (
      <Select<string>
        size="small"
        className="w-full"
        popupMatchSelectWidth={false}
        value={scope ?? NO_ACCESS_VALUE}
        disabled={mutation.isLoading}
        options={[
          { value: NO_ACCESS_VALUE, label: "Brak" },
          ...grantable.map((candidate) => ({ value: candidate, label: scopeLabels[candidate] })),
        ]}
        onChange={(value) =>
          mutation.mutate({
            role,
            key: permissionKey,
            scope: value === NO_ACCESS_VALUE ? null : (value as PermissionScopeEnum),
          })
        }
      />
    );
  };

  return (
    <Space direction="vertical" size="middle" className="w-full">
      <Typography.Paragraph type="secondary" className="mb-0">
        Macierz pokazuje, które podstrony panelu widzi każda rola. Rozwiń wiersz, aby ustawić szczegółowe uprawnienia i
        zakresy. Właściciel zawsze ma pełny dostęp. Zalogowani członkowie zobaczą zmiany po odświeżeniu sesji (do
        godziny lub po ponownym zalogowaniu).
      </Typography.Paragraph>

      {baselinesQuery.isError ? (
        <Alert type="error" showIcon message={(baselinesQuery.error as Error).message} />
      ) : null}
      {mutation.isError ? <Alert type="error" showIcon message={(mutation.error as Error).message} /> : null}

      <Table<RoleEditorArea>
        dataSource={[...roleEditorAreas]}
        loading={baselinesQuery.isLoading}
        pagination={false}
        rowKey="area"
        size="small"
        expandable={{
          expandedRowRender: (area) => (
            <Table<{ key: PermissionKeyEnum }>
              dataSource={area.keys.map((key) => ({ key }))}
              pagination={false}
              rowKey="key"
              size="small"
            >
              <Table.Column<{ key: PermissionKeyEnum }>
                title="Uprawnienie"
                key="permission"
                render={(_, row) => (
                  <Space direction="vertical" size={0}>
                    <Typography.Text>
                      {permissionLabels[row.key] ?? catalogByKey.get(row.key)?.description ?? row.key}
                    </Typography.Text>
                    <Typography.Text type="secondary" className="text-xs">
                      {row.key}
                    </Typography.Text>
                  </Space>
                )}
              />
              {EDITABLE_ROLES.map((role) => (
                <Table.Column<{ key: PermissionKeyEnum }>
                  title={roleLabels[role]}
                  key={role}
                  width={150}
                  render={(_, row) => renderScopeSelect(row.key, role)}
                />
              ))}
            </Table>
          ),
        }}
      >
        <Table.Column<RoleEditorArea> title="Obszar" key="label" dataIndex="label" />
        {EDITABLE_ROLES.map((role) => (
          <Table.Column<RoleEditorArea>
            title={roleLabels[role]}
            key={role}
            width={120}
            align="center"
            render={(_, area) => renderAreaCell(area, role)}
          />
        ))}
        <Table.Column<RoleEditorArea>
          title="Właściciel"
          key="owner"
          width={120}
          align="center"
          render={() => <Tag color="green">wszystko</Tag>}
        />
      </Table>
    </Space>
  );
};

export default TeamRolesTab;
